<template>
   <div class="home-page-container home-page-container-fix">
      <!-- Hero Banner-->
      <HeroBanner :config="homePageData.heroBannerslider" />

      <!-- Customers Logos-->
      <CustomersHome />

      <!-- Products Section  -->
      <section class="intro-band">
         <div class="intro-band__wrap">
            <p class="intro-band__eyebrow">Charts &amp; dashboards</p>
            <h2 class="intro-band__heading">
               Create interactive charts and dynamic dashboards for any platform
            </h2>
            <p class="intro-band__sub">
               From quick prototypes to enterprise dashboards, FusionCharts helps you build
               responsive, visually consistent charts in a few lines of code, integrated with the
               stack you already use.
            </p>
            <div class="intro-band__cta">
               <a
                  href="https://resources.fusioncharts.com/download-free-trial/"
                  class="fc-pill-btn fc-pill-btn--solid"
                  target="_self"
                  >Start your free trial</a
               >
               <a
                  href="https://cart.fusioncharts.com/"
                  class="fc-pill-btn fc-pill-btn--ghost"
                  target="_self"
                  >Upgrade now</a
               >
            </div>
         </div>
      </section>

      <!-- Chart Showcase -->
      <ChartShowcase id="chartShowCase1" :config="homePageData.chartShowcase" />
      <ChartShowcase id="chartShowCase2" :is-light="true" :config="homePageData.ftChartShowcase" />

      <!-- Product Comparison -->
      <section class="why-band">
         <ProductComparison :content="homePageData.comparison" />
      </section>

      <!-- Happy Customers -->
      <div class="container-fluid pt-2 pb-2">
         <HappyCustomer :config="homePageData.customersLogo" />
      </div>

      <!-- Testimonials -->
      <section class="simple-testimonial-container">
         <div class="custom-container-1440">
            <SimpleTestimonial :show-custom-btns="true" :content="homePageData.testimonial" />
         </div>
      </section>
   </div>
</template>

<script>
import { mapState } from 'vuex';
import HeroBanner from '../components/home/HeroBannerNew.vue';
import CustomersHome from '../components/home/CustomersHome.vue';
import ProductComparison from '../components/home/ProductComparison.vue';
import metaGenerator from '../util/meta.js';
import ChartShowcase from '../components/home/ChartShowcase.vue';
import HappyCustomer from '../components/pricing/HappyCustomer.vue';
import SimpleTestimonial from '../components/core/SimpleTestimonial.vue';
// import Card from '../components/core/Card.vue';

export default {
   name: 'HomeView',
   components: {
      HeroBanner,
      CustomersHome,
      // Card,
      SimpleTestimonial,
      ChartShowcase,
      HappyCustomer,
      ProductComparison,
   },
   data() {
      return {
         mixpanelHomeTracker: null,
      };
   },
   computed: {
      ...mapState(['homePageData']),
   },
   async asyncData({ store }) {
      await store.dispatch('HOME_PAGE_DATA_FETCH');
   },
   mounted() {
      // Safe tracker init (no optional chaining for older builds)
      if (process.client && this.$tracker && this.$tracker.home) {
         this.mixpanelHomeTracker = this.$tracker.home;
         this.mixpanelHomeTracker.visited();
      }
   },
   metaInfo() {
      const meta = metaGenerator(this.homePageData);
      // Moved here from index.template.html, which injected it
      // unconditionally on every page - the browser preloads an image every
      // other route never renders, and warns ("preloaded but not used
      // within a few seconds") on all of them. This is the homepage's own
      // mobile hero image, so it only belongs on this page's <head>.
      meta.link = (meta.link || []).concat({
         rel: 'preload',
         as: 'image',
         href: '/public/banners/Mobile_Charts_EDM_Graphic.webp',
         fetchpriority: 'high',
         imagesrcset: '/public/banners/Mobile_Charts_EDM_Graphic.webp',
         imagesizes: '100vw',
      });
      return meta;
   },
};
</script>

<style lang="scss" scoped>
.main-container {
   padding-top: 42px + 54px; /* 96px total */
   /* OR using calc */
   padding-top: calc(42px + 54px);
}
.simple-testimonial-container {
   background-color: #f6f7fb;
   border-top: 1px solid #d8dce6;
   border-bottom: 1px solid #d8dce6;
   padding: 80px 0;
}
.why-band {
   background-color: #f6f7fb;
   border-top: 1px solid #d8dce6;
   border-bottom: 1px solid #d8dce6;
}
</style>

<style lang="scss">
// Everything in this block is scoped under .home-page-container on purpose:
// ChartShowcase, SimpleTestimonial and the legacy .btn-primary-grad/
// .btn-outline-primary classes are all reused on several other pages
// (Comparison, ProductGallery, About, etc.) that haven't been redesigned -
// overriding the shared components/classes directly would change those
// pages too, not just the home page.
.home-page-container {
   // CTA buttons - match every button on this page to the intro-band's pill
   // style (.fc-pill-btn--solid/--ghost) instead of the site-wide legacy
   // .btn-primary-grad/.btn-outline-primary look.
   .btn {
      border-radius: 999px;
      padding: 12px 24px;
      font-family: basefontBold, 'Helvetica Neue', Arial, sans-serif;
      font-size: 15px;
      font-weight: 700;
      transition: filter 0.14s ease, transform 0.14s ease, background 0.14s ease, color 0.14s ease;
   }
   // .text-uppercase is a Bootstrap utility applied !important - needs the
   // same to actually override it.
   .btn.text-uppercase {
      text-transform: none !important;
   }
   .btn-primary-grad {
      background: linear-gradient(100deg, var(--fc-revamp-teal), var(--fc-revamp-teal-2));
      border: none;
      box-shadow: 0 8px 20px -10px rgba(18, 179, 186, 0.9);
      color: #fff;

      &:hover {
         background: linear-gradient(100deg, var(--fc-revamp-teal), var(--fc-revamp-teal-2));
         filter: brightness(1.06);
         transform: translateY(-1px);
         color: #fff;
      }
      // The legacy Bootstrap theme's own :focus box-shadow (a teal ring)
      // was never overridden, so it kept showing through on top of this on
      // click/tab, on both variants - looked like a stray gradient bleeding
      // out of the button. Reset it explicitly on both.
      &:focus {
         box-shadow: 0 8px 20px -10px rgba(18, 179, 186, 0.9);
      }
   }
   .btn-outline-primary {
      background: transparent;
      border: 1.5px solid var(--fc-revamp-indigo);
      // 1.5px less than .btn's 12px so the border doesn't add extra height
      // versus the border-less solid button next to it.
      padding: 8.5px 22px;
      color: var(--fc-revamp-indigo);

      // !important because the legacy theme has a more specific compound
      // selector for the pressed state - .btn-outline-primary:not(:disabled)
      // :not(.disabled):active - which otherwise still wins over a plain
      // .home-page-container .btn-outline-primary:active override and paints
      // its old teal background/border during an actual click, not just on
      // a plain hover.
      &:hover,
      &:active,
      &:not(:disabled):not(.disabled):active {
         background: var(--fc-revamp-indigo-soft) !important;
         border-color: var(--fc-revamp-indigo) !important;
         color: var(--fc-revamp-indigo) !important;
      }
      &:focus,
      &:not(:disabled):not(.disabled):active:focus {
         box-shadow: none !important;
      }
   }
}

.products,
.simple-testimonial-container {
   .custom-text-position {
      width: 80%;
   }
   @media only screen and (max-width: 1199px) {
      &.row-container {
         padding: 0 6rem;
      }
      .custom-text-position {
         width: 80%;
      }
   }
}

.products {
   .card-list-section__header {
      text-align: center;
      margin: 0 auto;
   }
}

.custom-container-1440 {
   max-width: 1200px;
   margin: 0 auto;
   padding: 0 30px;

   @media only screen and (max-width: 1024px) {
      max-width: 900px;
   }

   @media only screen and (max-width: 991px) {
      max-width: 890px;
   }

   @media only screen and (max-width: 799px) {
      padding: 0 36px;
   }
}

.card-list-section__cards-wrapper .icon-fcexp-4 {
   background: none;
   background-image: url(/public/images/FusionGrid-Icon-blue.jpg);
   width: 42px;
   height: 42px;
   background-repeat: no-repeat;
   background-position: center;
}
.chart-gallery-wrapper {
   padding: 60px 0 70px 0;
   &.white-bg {
      background-color: white;
   }

   h2.header {
      font-size: 32px;
      letter-spacing: 0.5px;
      line-height: 42px;
      text-align: center;
      width: 95%;
      margin: 0 auto 10px;
   }
   p.sub-heading {
      font-size: 16px;
      color: #686980;
      width: 920px;
      line-height: 24px;
      margin: 0 auto 50px auto;
      text-align: center;
      @media screen and (max-width: 950px) {
         width: 90%;
      }
      @media screen and (max-width: 1279px) {
         margin: 0 auto 30px auto;
      }
   }
}
.intro-band {
   padding: 82px 0;

   &__wrap {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 24px;
      text-align: center;
   }
   &__eyebrow {
      margin: 0;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.11em;
      text-transform: uppercase;
      color: #5b2fb0;
   }
   &__heading {
      margin: 12px 0 0;
      font-size: 32px;
      letter-spacing: 0.5px;
      line-height: 42px;
   }
   &__sub {
      margin: 14px auto 0;
      max-width: 760px;
      font-size: 16px;
      color: #6b7288;
      line-height: 1.65;
   }
   &__cta {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
      margin-top: 32px;
   }

   @media (max-width: 760px) {
      padding: 58px 0;
   }
   @media (max-width: 480px) {
      padding: 46px 0;

      .intro-band__cta {
         flex-direction: column;
         align-items: center;

         .fc-pill-btn {
            width: 100%;
            max-width: 280px;
         }
      }
   }
}
</style>
